import React, { useEffect, useRef, useState } from 'react'
import { connect, useDispatch, shallowEqual, useSelector } from 'react-redux'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import LeftArrow from '@tecuity/sos-icons/react/left-arrow'
import { setErrorToast, setSuccessToast } from '../../actions'
import { sendToSupervisor } from '../WorkviewManager/WorkviewActions'
import { ConfirmDialog } from '../Modal/Modal'
import { useHistory } from 'react-router'
import {
  validateAllSteps,
  validateAndSetActiveStep,
  saveRecord,
  createMetaCollection,
  createEncryptionCollection,
  createFieldOverridesCollection,
  buildFullWizardFieldIdList,
  resolveWizardAttributesLogic,
  setWizardSubmitting,
} from './WizardActions'
import { getFirstFocusableScopedElement, getFieldsByType } from 'utilities'
import { resolveFieldConditions } from '@tecuity/wizard-utilities'
import { resolveFieldLogic } from '../../store'
import deepEqual from 'deep-equal'
import { SET_RECEIPT_WORKVIEW_ITEM } from '../Accounting/ReceiptValidation/ReceiptValidationReducer'
import {
  ADD_RECEIPT_ITEM,
  DELETE_ASSOCIATED_AMENDMENT,
  DELETE_STAFF_WIZARD_DRAFT_BY_ID,
  SAVE_STAFF_WIZARD_DRAFT,
  APPROVE_FILING_BY_WORKVIEW_ITEM_ID,
  SAVE_STAFF_FILING_CORRECTION,
  UNLOCK_WORKVIEW_ITEM_BY_ID,
  GET_WORKVIEW_ITEM,
  GET_SERVICE_OF_PROCESS_FILING_IS_APPROVED,
  VALIDATE_SECONDARY_REVIEW,
  REASSIGN_ITEM_TO_INITIAL_REVIEWER_UPDATED,
  GET_APP_SETTING_BY_NAME,
  GET_RECEIPT_NUM_BY_IMAGE_ENVELOPE_ID,
  SET_TRADEMARK_TO_IMAGE_CROP,
  SET_TRADEMARK_TO_PENDING_PAYMENT,
} from '../../requests'
import * as WizardClasses from './WizardClasses'
import { Spinner } from '../Loaders/Loaders'
import deepCopy from 'deep-copy'
import './ProcessContextBar.scss'
import { ADD_PDF_TO_LIST, pdfTypes } from '../ReportViewer/ReportViewerReducer'
import { shouldPopulateEnvelopeReports } from '../ReportViewer/ReportViewerActions'
import { SET_CORRESPONDENCE_WORKVIEW_ITEM } from '../WVMCorrespondence/Correspondence2Reducer'
import Portal from 'components/Portal/Portal'
import { Modal } from '../Modal/Modal'
import { DoubleValidationErrors } from './DoubleValidationErrors'
import { SET_DOUBLE_VALIDATION_ERRORS } from './WizardReducer'
import { EntryTypeIds, FilingSourceTypeIds, ProcessingStatusIds } from '../../Utilities/Utilities'

const ProcessContextBar = ({
  amendmentNum,
  itemID,
  activeStep,
  setActiveStep,
  lastStep,
  isInSignatureMode,
  validateAndSetActiveStep,
  readOnly,
  userGroup,
  bypass,
  staffFilingCorrection = false,
  ...props
}) => {
  const history = useHistory()
  const [submitting, setSubmitting] = React.useState(false)
  const dispatch = useDispatch()
  const { staffFilingCorrectionInfo, selectedRow: workviewItem } = useSelector(
    (state) => state.workview2,
    shallowEqual
  )
  const { DLN, FileDate, EntryTypeName: entryType } = workviewItem
  const { windowInstance } = useSelector((state) => state.imageViewer, shallowEqual)
  const { appUserRights, appUserID } = useSelector((state) => state.auth, shallowEqual)
  const [savingDraft, setSavingDraft] = useState(false)
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false)
  const [unlockItem, setUnlockItem] = useState(false)
  const [doubleValidationModalOpen, setDoubleValidationModalOpen] = useState(false)
  const [confirmSendToSupervisor, setConfirmSendToSupervisor] = useState(false)
  // TRADEMARK-AMENDMENT ONLY. Opened solely by finishOrPromptCrop (which gates on trademark amendments).
  const [cropPrompt, setCropPrompt] = useState({ open: false, onYes: null, onNo: null, onDismiss: null })
  const [populateOnEnvelopeCompletion, setPopulateOnEnvelopeCompletion] = useState(false)
  const unlockItemRef = useRef()
  // Distinguishes a Yes/No button click from a modal dismissal (backdrop/X/Escape).
  const cropActionTakenRef = useRef(false)

  const nextStep = () => {
    setUnlockItem(false)
    if (readOnly) {
      setActiveStep(activeStep + 1)
    } else {
      validateAndSetActiveStep(activeStep + 1)
    }
  }
  const prevStep = () => {
    setUnlockItem(false)
    if (props.readOnly) {
      setActiveStep(props.activeStep - 1)
    } else {
      validateAndSetActiveStep(activeStep - 1)
    }
  }

  // Only display the Apostille web prepped report viewer when the
  // 'WebPreppedReportViewerDisplay' app setting exists and is set to true.
  // If the setting does not exist (null/error), the report viewer is not displayed.
  const displayApostilleReportViewer = (filingNum) => {
    GET_APP_SETTING_BY_NAME(
      'WebPreppedReportViewerDisplay',
      (data) => {
        if (data && String(data.VALUE).toLowerCase() === 'true') {
          dispatch({
            type: ADD_PDF_TO_LIST,
            pdfInfo: {
              filename: `Apostille-${filingNum}.pdf`,
              type: pdfTypes.APOSTILLE,
              apostilleNum: filingNum,
            },
          })
        }
      },
      (err) => {
        console.error(err)
      }
    )
  }

  const completeSubmission = (destination) => {
    bypass()
    dispatch(setSuccessToast('Application was submitted'))
    history.push(destination)
  }

  useEffect(() => {
    GET_APP_SETTING_BY_NAME(
      'PopulateReportViewerOnEnvelopeCompletion',
      (data) => {
        setPopulateOnEnvelopeCompletion(!!data && String(data.VALUE).toLowerCase() === 'true')
      },
      (err) => {
        console.error(err)
        setPopulateOnEnvelopeCompletion(false)
      }
    )
  }, [])

  useEffect(() => {
    unlockItemRef.current = unlockItem
  }, [unlockItem])

  useEffect(() => {
    return () => {
      if (unlockItemRef.current)
        UNLOCK_WORKVIEW_ITEM_BY_ID(
          workviewItem.WorkViewItemId,
          () => {
            dispatch(setSuccessToast('Unlocked Work Item'))
          },
          (err) => {
            console.error(err)
            // dispatch(setErrorToast('Failed to Unlock Work Item'))
          }
        )
    }
  }, [])

  const buildFieldJSON = () => {
    let fieldIds = buildFullWizardFieldIdList()
    let FIELD_VALUES = {}
    let DYNAMIC_DATA = []
    let FIELD_META = {}
    let fieldNameMap = buildFieldNameMap(fieldIds)
    fieldIds
      .filter((fieldId) => {
        const field = props.fields[fieldId]
        const fieldAfterLogicResolution = resolveFieldLogic(field)
        const fieldOnlyHiddenByLogic = field.active && !fieldAfterLogicResolution.active
        if (fieldOnlyHiddenByLogic) {
          return false
        }
        return true
      })
      .forEach((fieldId) => {
        let field = resolveFieldLogic(props.fields[fieldId])
        let kvPairs = new WizardClasses[field.fieldClass]().getKeyValuePairs(field, props.fields)
        kvPairs.forEach((pair) => {
          const fieldNameIsNotUnique = fieldNameMap[field.name] > 1
          const shouldUseFieldIdAsFieldName = field.disabled && !field.hideFieldOnForm
          // const metaKey = shouldUseFieldIdAsFieldName ? field.fieldId : field.name
          if (shouldUseFieldIdAsFieldName && fieldNameIsNotUnique) {
            FIELD_VALUES = { ...FIELD_VALUES, [field.fieldId]: [pair.value] }
            FIELD_META = { ...FIELD_META, [field.fieldId]: pair.meta }
          } else {
            if (FIELD_VALUES[pair.name]) {
              FIELD_VALUES = {
                ...FIELD_VALUES,
                [pair.name]: [...FIELD_VALUES[pair.name], pair.value],
              }
            } else {
              FIELD_VALUES = { ...FIELD_VALUES, [pair.name]: [pair.value] }
            }
            FIELD_META = { ...FIELD_META, [pair.name]: pair.meta }
          }
        })
        if (field.dynamic) {
          DYNAMIC_DATA.push(field.name)
        }
      })
    Object.keys(FIELD_VALUES).forEach((key) => {
      if (Array.isArray(FIELD_VALUES[key])) {
        if (FIELD_VALUES[key].length === 1) {
          FIELD_VALUES[key] = FIELD_VALUES[key][0]
        }
      }
    })
    FIELD_META = createMetaCollection(FIELD_META, props.wizard, fieldIds)
    const ENCRYPTION = createEncryptionCollection(props.wizard)
    const FIELD_OVERRIDES = createFieldOverridesCollection(props.wizard)
    const resolvedWizard = props.resolveWizardAttributesLogic()
    return {
      FIELD_VALUES,
      DYNAMIC_DATA,
      FIELD_META,
      ENCRYPTION,
      FIELD_OVERRIDES,
      WIZARD_OVERRIDES: getWizardOverrides(resolvedWizard),
    }
  }

  const createPayload = () => {
    const { FIELD_VALUES, DYNAMIC_DATA, FIELD_META, ENCRYPTION, FIELD_OVERRIDES } = buildFieldJSON()
    let webUser = JSON.parse(localStorage.getItem('USER'))
    const resolvedWizard = props.resolveWizardAttributesLogic()
    const payload = {
      METADATA: {
        FORM_DEFINITION_ID: props.formDefinitionId,
        WEB_USER_ID: webUser ? webUser.WEB_USER_ID : 0,
        SOURCE_ID: props.sourceId,
        SOURCE_TYPE_ID: props.sourceTypeId,
        AMENDMENT_NUM: props.amendmentNum || props.wizard.wizard.amendmentNum,
        DRAFT_ID: props.draftId ? +props.draftId : 0,
        DYNAMIC_DATA,
        INTERNAL_REVIEW_REQUIRED_YN:
          resolvedWizard.internalReviewRequired || props.calculateInternalReview(),
        REDACTION_REQUIRED_YN: calculateRedaction(),
        SecondaryReviewRequired: calculateRequiresSecondaryReview(),
        //WorkviewItem
        ...props.meta,
      },
      FIELD_VALUES,
      FIELD_META,
      ENCRYPTION,
      FIELD_OVERRIDES,
      WIZARD_OVERRIDES: getWizardOverrides(resolvedWizard),
    }

    return payload
  }

  const GENERIC_SERVER_ERROR = `Something went wrong while we were trying to \
                                save the filing. Try submitting the filing again, and if the problem persists \
                                please contact customer support.`

  const validateAndSaveStaffFilingCorrection = () => {
    dispatch(setWizardSubmitting(true))
    setSubmitting(true)
    return new Promise((resolve, reject) => {
      validateAllSteps((wizardValid, globalErrorMessages) => {
        if (wizardValid) {
          const payload = createPayload()
          const mergedPayload = {
            ...payload,
            METADATA: {
              ...payload.METADATA,
              FilingNum: staffFilingCorrectionInfo ? staffFilingCorrectionInfo.FilingNum : '',
            },
          }
          SAVE_STAFF_FILING_CORRECTION(
            mergedPayload,
            (filingNum) => {
              bypass()
              dispatch(setSuccessToast(`Successfully Saved Correction against ${filingNum}`))
              history.goBack()
              resolve(filingNum)
            },
            (err) => {
              setSubmitting(false)
              dispatch(setWizardSubmitting(false))
              dispatch(setErrorToast(`Failed to Save Correction`))
              console.error(err)
            }
          )
        } else {
          setSubmitting(false)
          dispatch(setWizardSubmitting(false))
          if (globalErrorMessages.length) {
            globalErrorMessages.forEach((error) => {
              dispatch(setErrorToast(error))
            })
          }
          reject(`There are errors in the wizard preventing submission.`)
          const focusable = getFirstFocusableScopedElement('.sidebar', '[aria-invalid="true"]')
          if (focusable) {
            focusable.focus()
          }
        }
      })
    })
  }

  const defaultWorkflow = (filingNum, resolve, reject, shouldCrop = true) => {
    ADD_RECEIPT_ITEM(
      {
        DLN: DLN,
        ImageEnvelopeID: workviewItem.ImageEnvelopeId,
        ImageGroupID: workviewItem.ImageGroupId,
        SourceID: workviewItem.Filing.Id,
        SourceTypeID: workviewItem.SourceTypeId,
        Bypass: workviewItem.EntryTypeId === EntryTypeIds.WebFiled,
      },
      (response) => {
        //Callack from receipt
        const maybePopulateEnvelopeReports = (approvalResponse) => {
          if (
            populateOnEnvelopeCompletion &&
            shouldPopulateEnvelopeReports({
              isEnvelopeComplete: approvalResponse?.IsEnvelopeComplete,
              hasReceipt: !!response.receipt,
              isReceiptBalanced: response.receipt?.Status === 'Balanced',
            })
          ) {
            dispatch({
              type: ADD_PDF_TO_LIST,
              pdfInfo: {
                filename: `Envelope-${workviewItem.ImageEnvelopeId}.pdf`,
                type: pdfTypes.IMAGE_ENVELOPE,
                imageEnvelopeId: workviewItem.ImageEnvelopeId,
              },
            })
          }
        }
        if (!response.receipt) {
          if (workviewItem.SourceTypeId === FilingSourceTypeIds.Trademark && shouldCrop) {
            bypass()
            dispatch(setSuccessToast('Application was submitted'))
            SET_TRADEMARK_TO_IMAGE_CROP(
              workviewItem.WorkViewItemId,
              () => history.push('/office/imageCropTool'),
              (err) => {
                console.error(err)
                dispatch(setErrorToast('Failed to set the trademark to Pending Image Crop.'))
                history.push('/office/workview')
              }
            )
            return
          }
          APPROVE_FILING_BY_WORKVIEW_ITEM_ID(
            workviewItem.WorkViewItemId,
            (approvalResponse) => {
              if(approvalResponse.ignoreReports) {
                bypass()
                dispatch(setSuccessToast('Application was submitted'))
                history.push('/office/workview')
                if (windowInstance && windowInstance.location && windowInstance.location.reload) {
                  windowInstance.location.reload() // This may cause the image viewer to close...
                  }
                  return;
              }

              if (populateOnEnvelopeCompletion) {
                if (
                  workviewItem.SourceTypeId === FilingSourceTypeIds.Business &&
                  workviewItem.EntryTypeId === 1119 /* Paper Filed */
                ) {
                  GET_RECEIPT_NUM_BY_IMAGE_ENVELOPE_ID(
                    workviewItem.ImageEnvelopeId,
                    (receiptNum) => {
                      maybePopulateEnvelopeReports(approvalResponse)
                      completeSubmission(
                        receiptNum && appUserRights.includes('ReceiptView')
                          ? `/accounting/receipt/${receiptNum}`
                          : '/office/workview'
                      )
                    },
                    (err) => {
                      console.error(err)
                      maybePopulateEnvelopeReports(approvalResponse)
                      completeSubmission('/office/workview')
                    }
                  )
                  return
                }
                maybePopulateEnvelopeReports(approvalResponse)
              } else {
              switch (workviewItem.SourceTypeId) {
                case FilingSourceTypeIds.Facsimile:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Facsimile-${filingNum}.pdf`,
                      type: pdfTypes.FACSIMILE,
                      facsimileNum: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.Notary:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Notary-${filingNum}.pdf`,
                      type: pdfTypes.NOTARY,
                      filingNum: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.Trademark:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Trademark-${filingNum}.pdf`,
                      type: pdfTypes.TRADEMARK,
                      trademarkAmendmentNumber: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.Business:
                  if (workviewItem.EntryTypeId === 1119 /* Paper Filed */) {
                    GET_RECEIPT_NUM_BY_IMAGE_ENVELOPE_ID(
                      workviewItem.ImageEnvelopeId,
                      (receiptNum) => {
                        dispatch({ type: ADD_PDF_TO_LIST,
                          pdfInfo: { filename: `Business-${workviewItem.IsInitialFiling ? 'Filing' : 'Amendment'}-${filingNum}.pdf`,
                          type: pdfTypes.BUSINESS_ENTITY, AmendmentNum: filingNum, ReceiptNum: receiptNum } })
                        completeSubmission(
                          receiptNum && appUserRights.includes('ReceiptView')
                            ? `/accounting/receipt/${receiptNum}`
                            : '/office/workview'
                        )
                      },
                      (err) => {
                        console.error(err)
                        completeSubmission('/office/workview')
                      }
                    )
                    return
                  }
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Business-${
                        workviewItem.IsInitialFiling ? 'Filing' : 'Amendment'
                      }-${filingNum}.pdf`,
                      type: pdfTypes.BUSINESS_ENTITY,
                      AmendmentNum: filingNum,
                  }})
                  break
                case FilingSourceTypeIds.Orders:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Business-Order-${filingNum}.pdf`,
                      type: pdfTypes.ORDER,
                      orderNum: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.Lien:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `UCC-${filingNum}.pdf`,
                      type: pdfTypes.UCC1,
                      uccAmendmentNumber: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.Apostille:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Apostille-${filingNum}.pdf`,
                      type: pdfTypes.APOSTILLE,
                      apostilleNum: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.ServiceOfProcess:
                  GET_SERVICE_OF_PROCESS_FILING_IS_APPROVED(
                    filingNum,
                    (isApproved) => {
                      if (isApproved === true || isApproved === 'true') {
                        const pdfInfo = {
                          type: pdfTypes.SERVICE_OF_PROCESS,
                          FilingNum: filingNum,
                          filename: `SOP-${filingNum}.pdf`,
                        }
                        dispatch({ type: ADD_PDF_TO_LIST, pdfInfo: { ...pdfInfo } })
                      }
                    },
                    (err) => console.error(err)
                  )
                  break
                case FilingSourceTypeIds.RegisteredAgent:
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `Registered-Agent-${filingNum}.pdf`,
                      type: pdfTypes.REGISTERED_AGENT,
                      registeredAgentNum: filingNum,
                    },
                  })
                  break
                case FilingSourceTypeIds.Charity:
                case FilingSourceTypeIds.Contractor:
                case FilingSourceTypeIds.Lobbyist:
                case FilingSourceTypeIds.AthleteAgent:
                  const sourceTypeName = Object.keys(FilingSourceTypeIds).find(x => FilingSourceTypeIds[x] === workviewItem.SourceTypeId) 
                  dispatch({
                    type: ADD_PDF_TO_LIST,
                    pdfInfo: {
                      filename: `${sourceTypeName}-${filingNum}.pdf`,
                      type: workviewItem.SourceTypeId,
                      filingNum: filingNum
                    }
                  })
                  break
                default:
                  console.warn(
                    `Source Type Id: ${workviewItem.SourceTypeId} does not generate report for free filings.`
                  )
                  dispatch(
                    setErrorToast(
                      `Source Type Id: ${workviewItem.SourceTypeId} does not generate report for free filings.`
                    )
                  )
                  break
              }
              }

              bypass()
              dispatch(setSuccessToast('Application was submitted'))
              history.push('/office/workview')
              if (windowInstance && windowInstance.location && windowInstance.location.reload) {
                windowInstance.location.reload() // This may cause the image viewer to close...
              }
            },
            (err) => {
              console.error(err)
              dispatch(setErrorToast(err.Message))
            }
          )
          return
        }
        else if ((response.receipt.Status === "Balanced" && workviewItem.EntryTypeId === 1118) ||
          (response.receipt.Status === "Balanced" && workviewItem.ProcessingStatusId === ProcessingStatusIds.PendingCountyReview))
        {
          if (workviewItem.SourceTypeId === FilingSourceTypeIds.Trademark && shouldCrop) {
            bypass()
            dispatch(setSuccessToast('Application was submitted and receipt is balanced.'))
            SET_TRADEMARK_TO_IMAGE_CROP(
              workviewItem.WorkViewItemId,
              () => history.push(`/office/imageCropTool`),
              (err) => {
                console.error(err)
                dispatch(setErrorToast('Failed to set the trademark to Pending Image Crop.'))
                history.push('/office/workview')
              }
            )
            return
          }
          APPROVE_FILING_BY_WORKVIEW_ITEM_ID(
            workviewItem.WorkViewItemId,
            (approvalResponse) => {
              if (populateOnEnvelopeCompletion) {
                maybePopulateEnvelopeReports(approvalResponse)
              } else {
                switch (workviewItem.SourceTypeId) {
                  case FilingSourceTypeIds.Apostille:
                    displayApostilleReportViewer(filingNum)
                    break
                  default:
                    break
                }
              }
              dispatch(setSuccessToast('Application was submitted and receipt is balanced.'))
              bypass()
              history.push('/office/workview')
            },
            (err) => {
              console.error(err)
              dispatch(setErrorToast(err.Message))
            })
        }
        else
        {
          dispatch(setSuccessToast('Application was submitted and receipt item has been created.'))
          bypass()
          if (workviewItem.SourceTypeId === FilingSourceTypeIds.Trademark && shouldCrop) {
            SET_TRADEMARK_TO_IMAGE_CROP(
              workviewItem.WorkViewItemId,
              () => history.push(`/office/imageCropTool`),
              (err) => {
                console.error(err)
                dispatch(setErrorToast('Failed to set the trademark to Pending Image Crop.'))
                history.push('/office/workview')
              }
            )
          } else {
            dispatch({
              type: SET_RECEIPT_WORKVIEW_ITEM,
              workviewItem: { ...workviewItem, Filing: { ...workviewItem.Filing, Num: filingNum } },
            })
            appUserRights.includes(`ReceiptView`) &&
              history.push(`/accounting/receipt/${response.receipt.ReceiptNum}`)
          }
        }
      },
      (err) => {
        console.error(err)
        DELETE_ASSOCIATED_AMENDMENT(
          DLN,
          () => {},
          (err) => {
            console.error(err.Message)
          }
        )
        if (err.Message.toLowerCase().includes('receipt')) {
          dispatch(
            setErrorToast(
              'The wizard as filled out requires a payment.' +
                'Please check if the wizard has been filled out correctly, and if it has, either reject or send for correction.'
            )
          )
        } else {
          dispatch(setErrorToast(err.message))
        }
      }
    )
    // DN Delete draft
    DELETE_STAFF_WIZARD_DRAFT_BY_ID(
      workviewItem.ImageGroupId,
      (res) => console.log(res),
      (err) => console.error(err)
    )
    //setSubmitting(false)
    dispatch(setWizardSubmitting(false))
    resolve(filingNum)
  }

  // TRADEMARK-AMENDMENT ONLY gate. This is the ONLY place that can open the crop prompt.
  // Every other source type -- and initial trademark filings -- call defaultWorkflow directly
  // (shouldCrop defaults to true), so their behavior is byte-for-byte unchanged and they can
  // never trigger this modal. When "No" is chosen, defaultWorkflow runs with shouldCrop=false,
  // which skips the trademark crop block and runs the exact original completion.
  const finishOrPromptCrop = (filingNum, resolve) => {
    const isTrademarkAmendment =
      workviewItem.SourceTypeId === FilingSourceTypeIds.Trademark && !workviewItem.IsInitialFiling
    if (!isTrademarkAmendment) {
      defaultWorkflow(filingNum, resolve)
      return
    }
    // Mark the item as awaiting crop the moment the decision opens, so a dismissal or crash
    // leaves it recoverable in the WVM as Pending Image Crop.
    cropActionTakenRef.current = false
    SET_TRADEMARK_TO_IMAGE_CROP(
      workviewItem.WorkViewItemId,
      () => {},
      (err) => console.error(err)
    )
    setCropPrompt({
      open: true,
      onYes: () => {
        cropActionTakenRef.current = true
        defaultWorkflow(filingNum, resolve, undefined, true)
      },
      onNo: () => {
        cropActionTakenRef.current = true
        // Not cropping: move it out of Pending Image Crop, then finish exactly as before.
        SET_TRADEMARK_TO_PENDING_PAYMENT(
          workviewItem.WorkViewItemId,
          () => defaultWorkflow(filingNum, resolve, undefined, false),
          (err) => {
            console.error(err)
            defaultWorkflow(filingNum, resolve, undefined, false)
          }
        )
      },
      // Dismissed (backdrop/X/Escape): leave it in Pending Image Crop and send back to the WVM.
      onDismiss: () => {
        bypass()
        resolve(filingNum)
        history.push('/office/workview')
      },
    })
  }

  const validateAndProcessWorkviewItem = () => {
    setUnlockItem(false)
    if (staffFilingCorrection) {
      return validateAndSaveStaffFilingCorrection()
    }
    dispatch(setWizardSubmitting(true))
    setSubmitting(true)
    return new Promise((resolve, reject) => {
      validateAllSteps((wizardValid, globalErrorMessages) => {
        if (wizardValid) {
          const payload = createPayload()
          let mergedPayload = {
            ...payload,
            METADATA: { ...payload.METADATA, DLN: DLN, ReceivedDate: FileDate },
          }
          if (
            workviewItem &&
            workviewItem.ProcessingStatusId === ProcessingStatusIds.PendingSecondaryReview
          ) {
            VALIDATE_SECONDARY_REVIEW(
              mergedPayload,
              workviewItem.Filing.Num,
              props.category,
              () => {
                finishOrPromptCrop(workviewItem.Filing.Num, resolve)
              },
              (e) => {
                if (Array.isArray(e)) {
                  dispatch({ type: SET_DOUBLE_VALIDATION_ERRORS, doubleValidationErrors: e })
                  setDoubleValidationModalOpen(true)
                } else {
                  console.error(e.Message)
                }
                setSubmitting(false)
                dispatch(setWizardSubmitting(false))
              }
            )
          } else {
            props.saveRecord(
              mergedPayload,
              props.category,
              props.mode,
              props.correctionMode,
              (filingNum) => {
                GET_WORKVIEW_ITEM(
                  workviewItem.WorkViewItemId,
                  false,
                  (item) => {
                    // The saveRecord updates the status of the WVItem,
                    // so if the same WVItem has the 'Pending Secondary Review' status
                    // then we just navigate to WVM.
                    if (item.ProcessingStatusId === ProcessingStatusIds.PendingSecondaryReview) {
                      dispatch(setSuccessToast(`DLN: ${item.DLN} is now Pending Secondary Review`))
                      bypass()
                      history.push('/office/workview')
                    } else finishOrPromptCrop(filingNum, resolve)
                  },
                  (err) => {
                    console.error(err)
                  }
                )
              },
              (e) => {
                console.error(e.Message || e.message)
                setSubmitting(false)
                dispatch(setWizardSubmitting(false))
                DELETE_ASSOCIATED_AMENDMENT(
                  DLN,
                  () => {},
                  (e) => {
                    console.error(e.Message)
                  }
                ) 
                if (e.displayInWizard) {
                  dispatch(setErrorToast(e.message))
                } else {
                  dispatch(setErrorToast(GENERIC_SERVER_ERROR))
                }
              }
            )
          }
        } else {
          setSubmitting(false)
          dispatch(setWizardSubmitting(false))
          if (globalErrorMessages.length) {
            globalErrorMessages.forEach((error) => {
              dispatch(setErrorToast(error))
            })
          }
          reject(`There are errors in the wizard preventing submission.`)
          const focusable = getFirstFocusableScopedElement('.sidebar', '[aria-invalid="true"]')
          if (focusable) {
            focusable.focus()
          }
        }
      })
    })
  }

  const getWizardOverrides = (wizard) => ({
    internalReviewRequired: wizard.internalReviewRequired,
    formTitle: wizard.formTitle,
    feeDescription: wizard.feeDescription,
  })

  const buildFieldNameMap = (fieldIds) =>
    fieldIds.reduce((obj, fieldId) => {
      const fieldName = props.fields[fieldId].name
      obj[fieldName] = (obj[fieldName] || 0) + 1
      return obj
    }, {})

  const calculateRequiresSecondaryReview = () => {
    let fields = buildFullWizardFieldIdList().map((fieldId) => props.fields[fieldId])
    let filteredFields = fields.filter((field) => {
      const fieldAfterLogicResolution = resolveFieldLogic(field)
      const fieldOnlyHiddenByLogic = field.active && !fieldAfterLogicResolution.active
      if (fieldOnlyHiddenByLogic) {
        return false
      }
      return true
    })
    return filteredFields.some((field) => field.requireDoubleValidation === true)
  }

  const calculateRedaction = () => {
    let fields = buildFullWizardFieldIdList().map((fieldId) => props.fields[fieldId])
    return fields.reduce((redacted, field) => {
      if (redacted) {
        return true
      } else {
        switch (field.type.toUpperCase()) {
          case 'REPEATER':
            return field.value.length > 0
              ? field.redact
                ? true
                : field.template.some((fid) => props.fields[fid].redact)
              : redacted
          default:
            return field.redact
        }
      }
    }, false)
  }

  const saveDraft = () => {
    setSaveDraftSuccess(false)
    setSavingDraft(true)
    let fieldValues = buildFieldJSON()
    let wizardState = deepCopy(props.wizardState)
    let recordNum = wizardState.wizard.meta.RECORD_NUM || ''
    delete wizardState.wizard.stepOrder
    delete wizardState.wizard.steps
    delete wizardState.wizard.sections
    delete wizardState.wizard.fields
    let draft = {
      UserID: appUserID,
      FormDefinitionID: props.formDefinitionId,
      WizardJson: fieldValues,
      WizardState: wizardState,
      FilingNum: workviewItem.RecordToAmend
        ? workviewItem.RecordToAmend['File #'] ||
          workviewItem.RecordToAmend['Filing Number'] ||
          workviewItem.RecordToAmend['Doc #'] ||
          workviewItem.RecordToAmend['Trademark #'] ||
          workviewItem.RecordToAmend['Record Number'] ||
          workviewItem.RecordToAmend['Commission Number']
        : recordNum,
      FilingTypeID: workviewItem.Record.TypeId || 0,
      ImageGroupID: workviewItem.ImageGroupId,
      FormTitle: props.formTitle || wizardState.wizard.formTitle || wizardState.wizard.title,
      AmendmentTypeID: workviewItem.Filing.TypeId || 0,
    }
    SAVE_STAFF_WIZARD_DRAFT(
      draft,
      (res) => {
        dispatch(setSuccessToast(res.message))
        setSavingDraft(false)
        setSaveDraftSuccess(true)
      },
      (err) => {
        dispatch(setErrorToast('An error occurred while saving the draft'))
        setSavingDraft(false)
      }
    )
  }

  const correctItem = () => {
    setUnlockItem(false)
    setTimeout(() => {
      dispatch({ type: SET_CORRESPONDENCE_WORKVIEW_ITEM, row: workviewItem })
      history.push('/office/correspondence/correct/' + itemID)
    }, 50)
  }

  const rejectItem = () => {
    setUnlockItem(false)
    setTimeout(() => {
      dispatch({ type: SET_CORRESPONDENCE_WORKVIEW_ITEM, row: workviewItem })
      history.push('/office/correspondence/reject/' + itemID)
    }, 50)
  }

  const cancelItem = () => {
    setUnlockItem(true)
    setTimeout(() => {
      if (staffFilingCorrection) history.goBack()
      history.push('/office/workview/')
    }, 50)
  }

  const supervisor = () => {
    setUnlockItem(true)
    setTimeout(() => {
      window.ALLOW_BYPASS_WIZARD_PROMPT = true
      dispatch(sendToSupervisor(workviewItem.WorkViewItemId))
      history.push('/office/workview/')      
    }, 50)
  }

  const sendToInitialReviewer = () => {
    setUnlockItem(false)
    if (workviewItem.previousReviewerUserId)
      REASSIGN_ITEM_TO_INITIAL_REVIEWER_UPDATED(
        workviewItem.WorkViewItemId,
        workviewItem.previousReviewerUserId,
        () => {
          dispatch(setSuccessToast('Successfully reassigned item to inital reviewer.'))
          bypass()
          history.push('/office/workview')
        },
        (err) => {
          console.error(err)
        }
      )
    else {
      console.error('No Previous Reviewer userId found, see Admin.')
      dispatch(setErrorToast('No Previous Reviewer userId found, see Admin.'))
    }
  }

  useEffect(() => {
    // remove checkmark 2 seconds after draft is saved
    let saveDraftTimeout
    if (saveDraftSuccess) {
      saveDraftTimeout = setTimeout(() => setSaveDraftSuccess(false), 2000)
    }
    return () => {
      clearTimeout(saveDraftTimeout)
    }
  }, [saveDraftSuccess])

  return (
    <div className="process-context-bar-wrapper context-bar-wrapper toolbar-context-wrapper">
      <ContextBar elevation="high">
        <div className="context-button-wrapper">
          <ContextButton disabled={!!submitting} onClick={cancelItem} variant="secondary">
            Cancel
          </ContextButton>
          {workviewItem.ProcessingStatusId === ProcessingStatusIds.PendingSecondaryReview && (
            <ContextButton disabled={!!submitting} onClick={sendToInitialReviewer} variant="danger">
              Send to Initial Reviewer
            </ContextButton>
          )}
          <ContextButton
            disabled={!!submitting}
            onClick={rejectItem}
            variant="danger"
            className={
              staffFilingCorrection ||
              !appUserRights.includes(`FileRejection`) ||
              !workviewItem.AllowRejections
                ? 'hidden'
                : ''
            }
          >
            Reject
          </ContextButton>
          <ContextButton
            disabled={!!submitting}
            onClick={correctItem}
            variant="secondary"
            className={
              staffFilingCorrection ||
              !appUserRights.includes(`FileCorrection`) ||
              !workviewItem.AllowCorrections ||
              workviewItem.ProcessingStatusId === ProcessingStatusIds.PendingSecondaryReview
                ? 'hidden'
                : ''
            }
          >
            Correct
          </ContextButton>
          {workviewItem.ProcessingStatusId !== ProcessingStatusIds.SupervisorReview && (
            <ContextButton
              disabled={!!submitting}
              onClick={() => setConfirmSendToSupervisor(true)}
              variant="secondary"
            >
              Supervisor Review
            </ContextButton>
          )}
          {workviewItem.SourceTypeId > 0 &&
            workviewItem.SourceTypeId !== FilingSourceTypeIds.Orders && (
              <ContextButton
                className={
                  'no-padding-context-button ' +
                  (entryType !== 'Paper Filed' ||
                  workviewItem.ProcessingStatusId === ProcessingStatusIds.PendingSecondaryReview ||
                  workviewItem.ProcessingStatusId === ProcessingStatusIds.SentToInitialReviewer
                    ? 'hidden'
                    : '')
                }
                variant="secondary"
                onClick={saveDraft}
                disabled={savingDraft}
              >
                Save Draft
                <div
                  className={`${saveDraftSuccess || savingDraft ? '' : 'hidden'} spinner-wrapper`}
                >
                  <input type="checkbox" id="check" disabled={true} checked={saveDraftSuccess} />
                  <label htmlFor="check">
                    <div className="check-icon"></div>
                  </label>
                </div>
              </ContextButton>
            )}
          <ContextButton
            className={activeStep === 0 || isInSignatureMode ? 'hidden' : ''}
            onClick={prevStep}
            disabled={!!submitting || activeStep <= 0 || isInSignatureMode}
            leftIcon={
              <div className="arrow-wrapper left">
                <LeftArrow />
              </div>
            }
            variant="secondary"
          >
            Previous
          </ContextButton>
          <ContextButton
            className={lastStep || isInSignatureMode ? 'hidden' : ''}
            style={{ paddingRight: '0px' }}
            onClick={nextStep}
            disabled={!!submitting || lastStep || isInSignatureMode}
            rightIcon={
              <div className="arrow-wrapper right">
                <RightArrow />
              </div>
            }
            variant="primary"
          >
            Next
          </ContextButton>
          <ContextButton
            className={!lastStep ? 'hidden' : ''}
            onClick={validateAndProcessWorkviewItem}
            disabled={!!submitting || !lastStep}
            rightIcon={
              <div className="arrow-wrapper right">
                <RightArrow />
              </div>
            }
            variant="primary"
          >
            Approve
          </ContextButton>
          {submitting && (
            <div style={{ marginTop: '20px', marginLeft: '20px', marginRight: '20px' }}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
        </div>
      </ContextBar>
      <Portal isOpened={doubleValidationModalOpen}>
        <Modal
          title="Failed Validation - Please Note Incorrect Entries"
          modalSize="large"
          cancelButton
          autoHeight
          closeModal={() => setDoubleValidationModalOpen(false)}
        >
          <DoubleValidationErrors />
        </Modal>
      </Portal>
      <Portal isOpened={confirmSendToSupervisor}>
        <ConfirmDialog
          closeModal={() => setConfirmSendToSupervisor(false)}
          onModalClosed={() => {}}
          message={process.env.WIZARD_ON_EARLY_EXIT_MESSAGE ||
                  `Are you sure you want to exit this form? Any unsaved data you have entered will be lost.\n\nSelect “OK” to exit, or Select “Cancel” to return to the form.`}
          buttons={[
            { label: 'Cancel', onClick: () => {}, className: 'btn-default' },
            {
              label: 'OK',
              onClick: supervisor,
              className: 'btn-danger',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
      {/* TRADEMARK-AMENDMENT ONLY. cropPrompt.open is set solely by finishOrPromptCrop (trademark
          amendments only); the isOpened check below ALSO re-verifies trademark, so no other
          module can ever render this modal. */}
      <Portal
        isOpened={
          cropPrompt.open && workviewItem.SourceTypeId === FilingSourceTypeIds.Trademark
        }
      >
        <ConfirmDialog
          closeModal={() => {
            const dismissed = !cropActionTakenRef.current
            const onDismiss = cropPrompt.onDismiss
            setCropPrompt({ open: false, onYes: null, onNo: null, onDismiss: null })
            if (dismissed && onDismiss) onDismiss()
          }}
          onModalClosed={() => {}}
          message={`Does this amendment need the trademark specimen cropped?`}
          buttons={[
            {
              label: 'No, finish',
              onClick: () => cropPrompt.onNo && cropPrompt.onNo(),
              className: 'btn-default',
            },
            {
              label: 'Yes, crop',
              onClick: () => cropPrompt.onYes && cropPrompt.onYes(),
              className: 'btn-primary',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </div>
  )
}

export default connect(
  (state) => {
    const filingMethod =
      state.wizard.requirePrintMailMethod.size > 0
        ? [{ value: { method: 'mail' } }]
        : getFieldsByType('UploadAndSubmit', state.wizard.wizard.fields)
    const activeFilingMethods =
      filingMethod &&
      filingMethod.filter(
        (field) =>
          resolveFieldConditions(
            field.conditions,
            state.wizard.wizard.fields,
            field.conditionsOperator
          ) && (field.logicNodes ? resolveFieldLogic(field).active : true)
      )
    const method = activeFilingMethods
      ? activeFilingMethods[0] && activeFilingMethods[0].value.method
      : null
    return {
      fields: state.wizard.wizard.fields,
      sourceId: state.wizard.wizard.sourceId,
      formDefinitionId: state.wizard.wizard.formDefinitionId,
      meta: state.wizard.wizard.meta,
      wizardTitle: state.wizard.wizard.title,
      onlineOverrideLabel: activeFilingMethods
        ? activeFilingMethods[0] && activeFilingMethods[0].onlineButtonLabel
        : '',
      printOverrideLabel: activeFilingMethods
        ? activeFilingMethods[0] && activeFilingMethods[0].printButtonLabel
        : '',
      signerOverrideLabel: activeFilingMethods
        ? activeFilingMethods[0] && activeFilingMethods[0].signerButtonLabel
        : '',
      activeUploadAndSubmitField: activeFilingMethods ? activeFilingMethods[0] || {} : {},
      validating: state.wizard.validating,
      draftId: state.wizard.draftId,
      savingDraft: state.wizard.savingDraft,
      readOnly: state.wizard.readOnly,
      mode: state.wizard.mode,
      amendmentNum: state.wizard.wizard.amendmentNum,
      requireStaffEntry: state.wizard.wizard.requireStaffEntry,
      showSignatures: state.wizard.wizard.showSignatures,
      preview: state.wizard.preview,
      docTitleSrc: state.wizard.wizard.docTitleSrc,
      docId: state.wizard.wizard.docId,
      formTitle: state.wizard.wizard.formTitle,
      filingMethod: method,
      category: state.wizard.wizard.category,
      correctionMode: state.wizard.correctionMode,
      wizardState: state.wizard,
      internalReviewRequired: state.wizard.wizard.internalReviewRequired,
      wizard: state.wizard,
      currentUserGroup: state.auth.USER_GROUP,
      feeDescription: state.wizard.wizard.feeDescription,
      uploading: state.wizard.uploading,
      entryTypeChanged: state.wizard.readOnly
        ? !deepEqual(state.wizard.diff.original.UPLOADS.method, method)
        : false,
      draftsDisabled: state.wizard.wizard.draftsDisabled,
      backOfficeOnly: state.wizard.wizard.backOfficeOnly,
      isMobile: state.app.isMobile,
      signatureValues: state.wizard.signatureValues,
      signature: state.wizard.signature,
      submitting: state.wizard.submitting,
    }
  },
  {
    validateAndSetActiveStep,
    validateAllSteps,
    saveRecord,
    resolveWizardAttributesLogic,
  }
)(ProcessContextBar)
